import { COLORS } from "colorTheme/themes";
import { extentTypeEnum, AO_MISSING_EVENT_ERROR } from "./consts";
import { HOUR_IN_SECONDS } from "../helpers/consts";
import { sortDirectionEnum } from "constants/consts";

const THRESHOLD_IN_PERCENT = 50;

export const mapStatusColors = status => {
  if (status >= 500) {
    return "red";
  } else if (status >= 400) {
    return "orange";
  } else if (status >= 200) {
    return "green";
  } else {
    return "grey";
  }
};

export const getErrorColor = errors => {
  if (errors > 0) {
    return "red";
  } else {
    return "grey";
  }
};

export const mapTypeName = type => {
  switch (type) {
    case extentTypeEnum.LOCAL_CODE:
      return "Local Code";
    case extentTypeEnum.REMOTE_CALL:
      return "Remote Calls";
    case extentTypeEnum.DATABASE_CALL:
      return "Database Calls";
    case extentTypeEnum.CACHE_CALL:
      return "Cache Calls";
  }
};

export const withDurationAndEndTime = (path, endTime) => {
  return `${path}?duration=${HOUR_IN_SECONDS}&end_time=${endTime}`;
};

export const mapIfUnrecognizedStatus = status => {
  if (!status || status === -1) {
    return "N/A";
  }
  return status;
};

export const getLayersBreakdownPercentages = extents => {
  if (extents.length === 0) {
    return {};
  }

  const breakdown = simplifyBreakdown(getLayersBreakdown(extents));

  if (breakdown.length === 0) {
    return {};
  }

  const totalDuration =
    breakdown[breakdown.length - 1].end - breakdown[0].start;

  const breakdownPercents = breakdown.map(({ type, start, end }) => ({
    type,
    duration: end - start,
  }));

  const spanSummaryDurations = breakdownPercents.reduce((acc, part) => {
    if (!acc[part.type]) {
      acc[part.type] = parseFloat(part.duration);
    } else {
      acc[part.type] = acc[part.type] + parseFloat(part.duration);
    }
    return acc;
  }, {});

  const layerPercentageList = Object.keys(spanSummaryDurations).map(key => {
    return {
      type: key,
      duration: spanSummaryDurations[key],
      percent: Number(
        spanSummaryDurations[key] === 0
          ? 0
          : (spanSummaryDurations[key] / totalDuration * 100).toFixed(2)
      ),
    };
  });

  let layerPercentage = {};

  layerPercentageList.forEach(item => {
    layerPercentage[item.type] = {
      duration: item.duration,
      percent: Number(item.percent),
    };
  });

  return layerPercentage;
};

export const getTypeColor = type => {
  if (type.includes(extentTypeEnum.REMOTE_CALL)) {
    return COLORS.PURPLE;
  } else if (type.includes(extentTypeEnum.DATABASE_CALL)) {
    return COLORS.WISTERIA_PURPLE;
  } else if (type.includes(extentTypeEnum.CACHE_CALL)) {
    return COLORS.RUSTY_RED;
  } else {
    return COLORS.BOSTON_BLUE;
  }
};

export const getLayersBreakdown = extents => {
  const startTimes = extents.map(e => ({
    spanId: e.spanId,
    parentId: e.parentId,
    start: e.startTime,
    type: e.type,
  }));

  const endTimes = extents.map(e => ({
    spanId: e.spanId,
    parentId: e.parentId,
    end: e.endTime,
    type: e.type,
  }));

  // Sort by timestamp. If equal sort by id
  const timestamps = sortTimestamps([...startTimes, ...endTimes], extents);

  // Iterate over the timestams and check in which points
  // the extent type has changed.
  const breakdown = timestamps.reduce((acc, cur) => {
    if (acc.length === 0) {
      return [
        {
          start: cur.start,
          type: cur.type,
          parent: cur.parent,
          parentId: cur.parentId,
        },
      ];
    }

    const last = acc[acc.length - 1];

    if (extents.length === 0) {
      return;
    }

    if (last.type !== cur.type) {
      // If a span of adifferent type just started
      // update the end type of the previous span
      // and append a new one. If the previous span
      // has the end time set already, seek parents
      // in order to fill the gap between two spans

      if (typeof cur.start === "number") {
        if (typeof last.end === "number") {
          const parent = extents.find(e => e.spanId === cur.parentId);

          if (parent) {
            acc.push(...spanWithParent(last, cur, parent));
          }
        } else {
          acc[acc.length - 1] = { ...last, end: cur.start };
          acc.push({
            start: cur.start,
            type: cur.type,
            parentId: cur.parentId,
          });
        }
      } else if (typeof cur.end === "number") {
        acc.push({
          start: last.end,
          end: cur.end,
          type: cur.type,
          parentId: cur.parentId,
        });
      }
    } else {
      // If the span are of the same type update
      // the end time of the current iteration.
      // If a new span started and the previous
      // has already finished, update the gap with
      // the parent type
      if (typeof cur.end === "number") {
        acc[acc.length - 1] = { ...last, end: cur.end };
      } else if (typeof cur.start === "number") {
        if (typeof last.end === "number") {
          const parent = extents.find(e => e.spanId === cur.parentId);

          if (parent) {
            acc.push(...spanWithParent(last, cur, parent));
          }
        }
      }
    }

    return acc;
  }, []);

  // Remove parts with the duration of 0s
  return breakdown.filter(part => part.start !== part.end);
};

const spanWithParent = (previous, current, parent) => [
  {
    start: previous.end,
    end: current.start,
    type: parent.type,
    parentId: parent.parentId,
  },
  {
    start: current.start,
    type: current.type,
    parentId: current.parentId,
  },
];

const isAbove = (a, b, extents) => {
  if (!b.parentId) {
    // b is root and has no parents :(
    return false;
  }
  const parent = extents.find(e => e.spanId === b.parentId);
  if (parent && parent.spanId === a.spanId) {
    return true; //a is above b
  } else if (parent === undefined) {
    return false;
  } else {
    return isAbove(a, parent, extents);
  }
};

const sortTimestamps = (timestamps, extents) =>
  timestamps.sort((a, b) => {
    const timestampA = a.end || a.start;
    const timestampB = b.end || b.start;

    if (timestampA === timestampB) {
      if (a.start && b.end) {
        return 1;
      } else if (a.end && b.start) {
        return -1;
      }
      const directionControl = a.end && b.end ? -1 : 1;
      if (isAbove(b, a, extents)) {
        return directionControl;
      } else if (isAbove(a, b, extents)) {
        return -1 * directionControl;
      } else {
        return 0;
      }
    } else {
      return timestampA - timestampB;
    }
  });

export const simplifyBreakdown = breakdown => {
  if (breakdown.length === 0) {
    return [];
  }

  let simplifiedBreakdown = [breakdown[0]];

  breakdown.forEach(part => {
    const last = simplifiedBreakdown[simplifiedBreakdown.length - 1];

    if (part.type === last.type) {
      last.end = part.end;
    } else {
      simplifiedBreakdown = [...simplifiedBreakdown, part];
    }
  });

  return simplifiedBreakdown;
};

export const getSlowElement = extents => {
  const durations = getLayersBreakdownPercentages(extents);

  if (Object.keys(durations).length === 0) {
    return {};
  }

  const traceDuration = extents[0].endTime - extents[0].startTime;
  let outcome = {};

  let longestLayer = { duration: 0, type: extentTypeEnum.LOCAL_CODE };
  Object.keys(durations).forEach(type => {
    if (longestLayer.duration <= durations[type].duration) {
      longestLayer = {
        type: type,
        duration: durations[type].duration,
        percent: durations[type].percent,
      };
    }
  });

  if (
    longestLayer.type === extentTypeEnum.LOCAL_CODE ||
    longestLayer.percent < THRESHOLD_IN_PERCENT
  ) {
    return {
      reason: "type",
      data: {
        type: extentTypeEnum.LOCAL_CODE,
        durationInPercent: durations[extentTypeEnum.LOCAL_CODE].percent || 0,
      },
    };
  }

  const slowExtent = getSlowExtent(extents, longestLayer.type, traceDuration);
  if (slowExtent) {
    outcome = {
      reason: "span",
      data: {
        id: slowExtent.id,
        spanId: slowExtent.spanId,
        type: slowExtent.type,
        layer: slowExtent.layer,
        durationInPercent:
          (slowExtent.endTime - slowExtent.startTime) * 100 / traceDuration,
      },
    };
  } else {
    outcome = {
      reason: "type",
      data: {
        type: longestLayer.type,
        durationInPercent: durations[longestLayer.type].percent,
      },
    };
  }

  return outcome;
};

const getSlowExtent = (extents, type, traceDuration) => {
  return extents
    .filter(e => e.type === type)
    .find(
      e => e.endTime - e.startTime > THRESHOLD_IN_PERCENT / 100 * traceDuration
    );
};

export const aggregateElements = (data, groupByKeys) => {
  let aggregatedData = [];

  data.forEach(cur => {
    let foundElement;

    aggregatedData.forEach(item => {
      let countMatches = 0;
      groupByKeys.forEach(name => {
        if (item[name] === cur[name]) {
          countMatches += 1;
        }
      });

      if (countMatches === groupByKeys.length) {
        foundElement = item;
      }
    });

    if (!foundElement) {
      return aggregatedData.push(
        Object.assign({}, cur, { count: 1, avg: cur.total })
      );
    }

    const dataKeys = Object.keys(data[0]).filter(
      key => !groupByKeys.includes(key)
    );
    dataKeys.map(
      keyFromData => (foundElement[keyFromData] += cur[keyFromData])
    );

    foundElement.count += 1;
    foundElement.avg = foundElement.total / foundElement.count;
  });
  return aggregatedData;
};

export const getValueWithUnit = (value, unit) => {
  if (isNaN(value) || !unit) {
    return value;
  }
  let valueWithComma;

  if (value >= 1000) {
    valueWithComma = parseFloat(value).toLocaleString("en-US");
  }

  return `${valueWithComma || value} ${unit}`;
};

export const sortItemsByKey = (items, key, direction) => {
  if (!key || !items.some(i => i[key])) {
    return items;
  }

  const sorted = items.concat().sort((a, b) => {
    // if ASC, move null values to the top
    if (!a[key]) {
      return -1;
    } else if (!b[key]) {
      return 1;
    }

    const x = a[key].raw || a[key].value;
    const y = b[key].raw || b[key].value;

    // sort numbers
    if (!isNaN(x) && !isNaN(y)) {
      return x - y;
    }

    // sort alphabetically
    return x.toString().localeCompare(y.toString());
  });

  return direction === sortDirectionEnum.ASCENDING ? sorted : sorted.reverse();
};

export const parseErrors = extents => {
  if (!extents || extents.length === 0) {
    return [];
  }

  const extentsWithErrors = extents.filter(
    item => item && item.errors.length > 0
  );

  const errors = extentsWithErrors.map(item => item.errors).flat();
  return errors.filter(error => error.class !== AO_MISSING_EVENT_ERROR);
};
